import { debounce } from "lodash"
import { AppDispatch } from "../common/types"
import { LiveUpdateEnvelope, LiveUpdateTopic } from "./types"

/**
 * A handler applies a batch of payloads for one event kind.
 *
 * Handlers receive an array rather than a single payload because incoming messages are coalesced:
 * emitters may publish one message per changed record, so a bulk server-side operation arrives as a
 * burst. Buffering turns that burst into a single dispatch and one re-render.
 */
export type LiveUpdateHandler<TPayload = unknown> = (payloads: TPayload[], dispatch: AppDispatch) => void

const COALESCE_WINDOW_MS = 100

// Several parts of the UI can care about the same event kind -- a grid reading redux and a modal
// holding its own copy of the rows both need signature state -- so each event has a set of
// handlers rather than one. Keyed by topic and event together, not event alone, so the same event
// name on two different topics can never cross-deliver.
const handlers: Record<string, Set<LiveUpdateHandler<never>>> = {}
const buffered: Record<string, unknown[]> = {}

const handlerKey = (topic: LiveUpdateTopic, event: string): string => `${topic}:${event}`

/**
 * Subscribe to an event kind on a topic for as long as the caller needs it.
 * @param topic The envelope's `topic`
 * @param event The envelope's `event` name
 * @param handler Applies a coalesced batch of that event's payloads
 * @returns Unsubscribes the handler. Callers own this: a component must call it on unmount, or it
 * keeps receiving events against stale state.
 */
export const registerLiveUpdateHandler = <TPayload>(topic: LiveUpdateTopic, event: string, handler: LiveUpdateHandler<TPayload>): (() => void) => {
    const key = handlerKey(topic, event)
    const typedHandler = handler as LiveUpdateHandler<never>
    if (!handlers[key]) handlers[key] = new Set()
    handlers[key].add(typedHandler)
    return () => {
        handlers[key]?.delete(typedHandler)
    }
}

/** Clear all registrations. Exists for tests, which would otherwise leak handlers between cases. */
export const resetLiveUpdateHandlers = (): void => {
    Object.keys(handlers).forEach(key => delete handlers[key])
    Object.keys(buffered).forEach(key => delete buffered[key])
    flush.cancel()
}

const flush = debounce((dispatch: AppDispatch) => {
    Object.entries(buffered).forEach(([key, payloads]) => {
        delete buffered[key]
        if (!payloads.length) return // Copied before iterating: a handler may unsubscribe itself while being called.
        ;[...(handlers[key] ?? [])].forEach(handler => handler(payloads as never[], dispatch))
    })
}, COALESCE_WINDOW_MS)

/**
 * Buffer an incoming envelope for its handlers, dropping events nothing has subscribed to.
 *
 * Unknown events are ignored rather than logged loudly: the channel is multiplexed, so a client
 * can legitimately receive an event kind a newer server publishes and it knows nothing about.
 * @param envelope The parsed message
 * @param dispatch Redux dispatch, passed through to the handlers on flush
 */
export const receiveLiveUpdate = (envelope: LiveUpdateEnvelope, dispatch: AppDispatch): void => {
    if (!envelope?.topic || !envelope?.event) return
    const key = handlerKey(envelope.topic, envelope.event)
    if (!handlers[key]?.size) return
    if (!buffered[key]) buffered[key] = []
    buffered[key].push(envelope.payload)
    flush(dispatch)
}
